import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Badge from '../common/Badge';
import Button from '../common/Button';
import WishlistButton from '../wishlist/WishlistButton';
import { useCart } from '../../hooks/useCart';
import { theme } from '../../config/theme';
import { 
  getPrimaryImage, 
  formatProductPrice, 
  getDiscountText, 
  getStockStatus 
} from '../../utils/productHelpers';
import { motion } from 'framer-motion';

const ProductCard = ({ product, onClick, showActions = false, onEdit, onDelete }) => {
  const navigate = useNavigate();
  const { isAuthenticated, isCustomer } = useAuth();
  const { addToCart, items, updateQuantity, removeFromCart, loading: cartLoading } = useCart();
  const stockStatus = getStockStatus(product);
  const discountText = getDiscountText(product);
  
  const [isAdding, setIsAdding] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const cartItem = items?.find(item => item.product?.id === product.id);

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    setIsAdding(true);
    try {
      await addToCart(product.id, 1);
    } finally {
      setIsAdding(false);
    }
  };

  const handleDecreaseQuantity = async (e) => {
    e.stopPropagation();
    if (!cartItem) return;

    if (cartItem.quantity <= 1) {
      setIsRemoving(true);
      try {
        await removeFromCart(cartItem.id);
      } finally {
        setIsRemoving(false);
      }
    } else {
      updateQuantity(cartItem.id, cartItem.quantity - 1);
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick(product);
    } else {
      navigate(`/products/${product.slug}`);
    }
  };

  const cardStyles = {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    overflow: 'hidden',
    border: `1px solid ${theme.colors.gray[200]}`,
    cursor: onClick ? 'pointer' : 'default',
    transition: theme.transitions.base,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  };

  const imageContainerStyles = {
    position: 'relative',
    width: '100%',
    height: '200px',
    backgroundColor: theme.colors.gray[50],
    overflow: 'hidden',
  };

  const imageStyles = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };

  const contentStyles = {
    padding: theme.spacing.md,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  };

  const titleStyles = {
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.semibold,
    marginBottom: theme.spacing.xs,
    color: theme.colors.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  };

  const priceStyles = {
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.primary.main,
    marginTop: 'auto',
    marginBottom: theme.spacing.sm,
  };

  const comparePriceStyles = {
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.text.secondary,
    textDecoration: 'line-through',
    marginLeft: theme.spacing.sm,
  };

  return (
    <motion.div 
      style={cardStyles}
      onClick={handleClick}
      whileHover={onClick ? { y: -8, boxShadow: theme.shadows.lg, borderColor: theme.colors.primary.light } : {}}
      transition={{ duration: 0.2 }}
    >
      <div style={imageContainerStyles}>
        <img 
          src={getPrimaryImage(product)} 
          alt={product.name}
          style={imageStyles}
        />
        
        {/* Wishlist Button - Top Left */}
        {isAuthenticated && isCustomer && !showActions && (
          <div style={{
            position: 'absolute',
            top: theme.spacing.sm,
            left: theme.spacing.sm,
          }}>
            <WishlistButton productId={product.id} variant="icon" size="md" />
          </div>
        )}

        {/* Discount Badge - Top Right */}
        {discountText && (
          <div style={{
            position: 'absolute',
            top: theme.spacing.sm,
            right: theme.spacing.sm,
            backgroundColor: theme.colors.error,
            color: theme.colors.white,
            padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
            borderRadius: theme.borderRadius.md,
            fontSize: theme.typography.fontSize.xs,
            fontWeight: theme.typography.fontWeight.bold,
          }}>
            {discountText}
          </div>
        )}

        {!product.is_active && (
          <div style={{
            position: 'absolute',
            bottom: theme.spacing.sm,
            left: theme.spacing.sm,
          }}>
            <Badge variant="error">Inactive</Badge>
          </div>
        )}
      </div>

      <div style={contentStyles}>
        <h3 style={titleStyles}>{product.name}</h3>
        
        {product.category && (
          <div style={{
            fontSize: theme.typography.fontSize.xs,
            color: theme.colors.text.secondary,
            marginBottom: theme.spacing.sm,
          }}>
            {product.category.name}
          </div>
        )}

        <div style={{ marginBottom: theme.spacing.sm }}>
          <Badge variant={stockStatus.color === 'success' ? 'success' : stockStatus.color === 'warning' ? 'warning' : 'error'}>
            {stockStatus.label}
          </Badge>
        </div>

        <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: theme.spacing.md }}>
          <span style={priceStyles}>
            {formatProductPrice(product.price)}
          </span>
          {product.compare_price && (
            <span style={comparePriceStyles}>
              {formatProductPrice(product.compare_price)}
            </span>
          )}
        </div>

        {showActions ? (
          <div style={{ 
            display: 'flex', 
            gap: theme.spacing.sm, 
            marginTop: 'auto',
          }}>
            <Button
              variant="outline"
              size="sm"
              fullWidth
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.(product);
              }}
            >
              Edit
            </Button>
            <Button
              variant="danger-outline"
              size="sm"
              fullWidth
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(product);
              }}
            >
              Delete
            </Button>
          </div>
        ) : (
          isAuthenticated && isCustomer ? (
            cartItem ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', gap: theme.spacing.sm }}>
                <Button 
                  variant="outline"
                  onClick={handleDecreaseQuantity}
                  disabled={isRemoving}
                  style={{ width: '40px', padding: 0, justifyContent: 'center' }}
                  loading={isRemoving}
                >
                  {isRemoving ? '' : '-'}
                </Button>
                <span style={{ fontWeight: theme.typography.fontWeight.semibold }}>{cartItem.quantity}</span>
                <Button 
                  variant="primary"
                  onClick={(e) => { e.stopPropagation(); updateQuantity(cartItem.id, cartItem.quantity + 1); }}
                  disabled={stockStatus.status === 'out_of_stock'}
                  style={{ width: '40px', padding: 0, justifyContent: 'center' }}
                >
                  +
                </Button>
              </div>
            ) : (
              <Button 
                variant="primary"
                size="sm" 
                fullWidth
                onClick={handleAddToCart}
                loading={isAdding}
                disabled={stockStatus.status === 'out_of_stock' || isAdding}
                style={{ marginTop: 'auto' }}
              >
                {isAdding ? 'Adding...' : 'Add to Cart'}
              </Button>
            )
          ) : (
            <Button
              variant="outline"
              size="sm"
              fullWidth
              onClick={(e) => {
                e.stopPropagation();
                navigate('/login');
              }}
              style={{ marginTop: 'auto' }}
            >
              Add to Cart
            </Button>
          )
        )}
      </div>
    </motion.div>
  );
};

export default ProductCard;